import { Outlet, useLocation, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useMe } from "@/features/auth/useMe";
import { getAdminTeams } from "@/lib/api";
import { unscopedKey } from "@/lib/queryClient";
import {
  UploadQueueProvider,
  useUploadQueueContext,
} from "@/features/coach/components/UploadQueueContext";
import { UploadQueuePanel } from "@/features/coach/components/UploadQueuePanel";
import { AdminSidebar } from "./components/AdminSidebar";
import type { AdminSectionId } from "./nav";
import {
  ADMIN_SECTIONS,
  adminConsoleTitle,
  canSeeAdminSection,
  defaultAdminLanding,
  hasAdminAccess,
  LEGACY_SECTION_REDIRECTS,
} from "./nav";

/**
 * M8a F1 / F3 / F4 / F5 / F9 — the /admin parent shell.
 *
 * Lifecycle responsibilities:
 *   - Show inline-login if `/api/me` is 401 (M4 pattern; replaced
 *     by the real M9 login UI later).
 *   - Redirect away from /admin entirely if the user has no admin
 *     access (plain coach / viewer).
 *   - Redirect /admin (root) → the user's default landing (global
 *     admin → overview, team admin → people, uploader → matches).
 *   - Honour LEGACY_SECTION_REDIRECTS (/admin/streams → /admin/live,
 *     /admin/system → /admin/performance).
 *   - Render the always-visible header (title + status strip),
 *     sidebar, and sub-route outlet.
 *
 * The status strip is mounted HERE, not per sub-route, so navigation
 * between admin pages doesn't refetch the 10 s diagnostics + streams
 * loops (one cache key, shared across the shell).
 */
export function AdminShell() {
  const me = useMe();
  const location = useLocation();
  const navigate = useNavigate();
  const pathname = location.pathname;
  const isAdminPath = pathname === "/admin" || pathname.startsWith("/admin/");

  // M9a A8 — auth gate. Unauthenticated users redirect to
  // `/login?next=<current path>` so they land back on the admin
  // page they were trying to reach. Replaces the M4
  // <InlineLoginPanel> that previously rendered inline.
  //
  // Capture the ORIGINAL pathname at mount via a ref so a second
  // fire of this effect (after the navigate updates `pathname`)
  // doesn't overwrite `?next=` with `/login`. TanStack Router
  // keeps the parent route mounted briefly across navigation,
  // and `useLocation` updates before the unmount.
  const authGateMountPath = useRef(pathname);
  const authRedirectFired = useRef(false);
  useEffect(() => {
    if (me.isLoading) return;
    if (!isAdminPath) return;
    if (me.data?.user) return;
    if (authRedirectFired.current) return;
    authRedirectFired.current = true;
    void navigate({
      to: "/login",
      search: { next: authGateMountPath.current || "/admin" },
      replace: true,
    });
  }, [me.isLoading, me.data, isAdminPath, navigate]);

  // F5 — legacy URL redirects.
  useEffect(() => {
    const redirect = LEGACY_SECTION_REDIRECTS[pathname];
    if (redirect) {
      void navigate({ to: redirect, replace: true });
    }
  }, [pathname, navigate]);

  // M9b D2 — auto-redirect to /welcome on a fresh tenant. A global
  // admin who lands on /admin with no admin-visible teams (system
  // has never had any team) and hasn't skipped the wizard gets
  // bounced to `/welcome` so the setup flow handles team / season /
  // invite creation. Skipping the wizard sets
  // `localStorage.tktic_welcome_skipped=1` so this effect doesn't
  // re-fire.
  //
  // Note on the cardinality check: a global admin can have ZERO
  // memberships of their own while the system has many teams
  // (admin-global has no membership row by design — they rely on
  // the global-admin override). So we don't gate on `me.teams`
  // alone; we issue a tiny `GET /api/admin/teams` and only redirect
  // when the system itself is empty. The query is `enabled` only
  // for global admins so non-admins never trigger it.
  const adminTeamsForWelcome = useQuery({
    queryKey: unscopedKey("admin-teams-welcome-probe"),
    queryFn: getAdminTeams,
    enabled: !!me.data?.user?.is_global_admin,
    // Tiny payload; refetch every visit is fine but cache the
    // result for the admin session so we don't double-fire.
    staleTime: 60_000,
  });
  const welcomeRedirectFired = useRef(false);

  // Centralized decision: should the welcome redirect own the next
  // navigation for this user? Computed BEFORE F4 (default landing)
  // so F4 can short-circuit when this is true — otherwise both
  // effects race in the same flush, F4 schedules /admin/overview
  // AFTER /welcome, and the welcome ref is already toggled so the
  // condition can never recover. Reviewer-flagged on PR #19.
  //
  // Cardinality probe ignores `default-team`: that row is a backend
  // bootstrap fallback for the legacy unscoped uploader path (see
  // `services/teams.list_teams` — surfaces it only when no real team
  // exists so the admin tenant lists aren't empty). For the welcome
  // redirect, a global admin who has only the default team has not
  // actually onboarded anything — they should still be bounced to
  // `/welcome` to create their first real team.
  const realTeamCount = (adminTeamsForWelcome.data ?? []).filter(
    (t) => t.slug !== "default-team",
  ).length;
  let welcomePending = false;
  if (
    isAdminPath &&
    me.data?.user?.is_global_admin &&
    !welcomeRedirectFired.current &&
    !adminTeamsForWelcome.isLoading &&
    !adminTeamsForWelcome.isError &&
    realTeamCount === 0
  ) {
    let skipped = false;
    try {
      skipped = localStorage.getItem("tktic_welcome_skipped") === "1";
    } catch {
      // private mode / quota — fall through; the wizard's own
      // setItem will fail too, so the user can skip again next
      // time without infinite-loop risk.
    }
    if (!skipped) welcomePending = true;
  }

  // M9b D2 — auto-redirect to /welcome on a fresh tenant. A global
  // admin who lands on /admin with no admin-visible teams (system
  // has never had any team) and hasn't skipped the wizard gets
  // bounced to `/welcome` so the setup flow handles team / season /
  // invite creation. Skipping the wizard sets
  // `localStorage.tktic_welcome_skipped=1` so this effect doesn't
  // re-fire.
  //
  // Note on the cardinality check: a global admin can have ZERO
  // memberships of their own while the system has many teams
  // (admin-global has no membership row by design — they rely on
  // the global-admin override). So we don't gate on `me.teams`
  // alone; we issue a tiny `GET /api/admin/teams` and only redirect
  // when the system itself is empty. The query is `enabled` only
  // for global admins so non-admins never trigger it.
  useEffect(() => {
    if (!welcomePending) return;
    welcomeRedirectFired.current = true;
    void navigate({ to: "/welcome", replace: true });
  }, [welcomePending, navigate]);

  // F4 — default landing for bare /admin. Skipped while the welcome
  // redirect owns the next navigation (would otherwise overwrite
  // /welcome with /admin/overview in the same flush).
  useEffect(() => {
    if (pathname !== "/admin") return;
    if (!me.data) return;
    if (welcomePending) return;
    const landing = defaultAdminLanding(me.data);
    const section = ADMIN_SECTIONS.find((s) => s.id === landing);
    if (section) {
      void navigate({ to: section.path, replace: true });
    }
  }, [pathname, me.data, welcomePending, navigate]);

  // F3 — hard gate: a signed-in user with no admin access gets
  // bounced to `/hub`, the role-based redirect, so they land on
  // their OWN home surface (coach → /coach/review, player/guardian
  // → /feedback) instead of the public marketing landing at `/`.
  // `me.data` is truthy here (the auth gate above already handled
  // anonymous users), so `/hub` always resolves to a real surface.
  useEffect(() => {
    if (!isAdminPath) return;
    if (!me.data) return;
    if (!hasAdminAccess(me.data)) {
      void navigate({ to: "/hub", replace: true });
    }
  }, [isAdminPath, me.data, navigate]);

  // Re-review P2 — per-section gate. The sidebar hides links the
  // user can't see, but a direct URL nav (or a stale bookmark) to
  // `/admin/live` still mounts the route and starts its admin
  // queries. Redirect denied sections to the user's default landing
  // so manual URLs follow the same gates as the sidebar. Skip
  // legacy-redirect paths (the other effect rewrites them first)
  // and the bare-/admin redirect (the F4 effect handles it).
  useEffect(() => {
    if (!me.data) return;
    if (!isAdminPath) return;
    if (pathname === "/admin" || pathname === "/admin/") return;
    if (LEGACY_SECTION_REDIRECTS[pathname]) return;
    const section = activeSectionForPath(pathname);
    if (!canSeeAdminSection(section, me.data)) {
      const landing = defaultAdminLanding(me.data);
      const target = ADMIN_SECTIONS.find((s) => s.id === landing);
      if (target && target.path !== pathname) {
        void navigate({ to: target.path, replace: true });
      }
    }
  }, [pathname, isAdminPath, me.data, navigate]);

  if (me.isLoading) {
    return (
      <div data-testid="admin-shell-loading" className="p-6 text-ink-muted">
        Loading…
      </div>
    );
  }

  // 401 boundary — the M9a `/login?next=` redirect effect above
  // fires. Render nothing rather than flashing the shell to an
  // anonymous user.
  if (!me.data?.user) {
    return null;
  }

  if (!hasAdminAccess(me.data)) {
    // The redirect effect above is firing; render nothing rather
    // than flashing the shell to a user who can't see it.
    return null;
  }

  const active = activeSectionForPath(pathname);

  return (
    // UploadQueueProvider hoists the multi-upload queue above the
    // `<Outlet />` so it survives navigation between /admin sections —
    // the queue's in-memory File objects (and the active chunk loop)
    // would otherwise be dropped every time AdminMatchesPage unmounts.
    <UploadQueueProvider>
      <main data-testid="admin-shell" className="min-h-screen bg-canvas">
        {/* The redundant "ADMIN CONSOLE" title strip was removed once the
         * global `PublicAppHeader` mounted on every route — the active
         * Admin link in that header already conveys the operator context,
         * and stacking two header bars produced the double-chrome the PR
         * review flagged. Operator identity / multi-team note still lives
         * in the screen-reader fallback below for the title computation. */}
        <h1 className="sr-only">{adminConsoleTitle(me.data)}</h1>
        {/* Sidebar + content sit in one flex row that starts directly
         * below the global header, so the sidebar background reaches
         * all the way up to the navbar with no gap above it. The team
         * scope bar lives INSIDE the content column (top-right) rather
         * than as a full-width band above this row — a band there
         * would push the sidebar down and leave a strip of canvas
         * above it. */}
        <div className="flex min-h-screen flex-col items-stretch md:flex-row">
          <AdminSidebar active={active} />
          <section
            data-testid="admin-content"
            data-admin-section={active}
            className="min-w-0 flex-1 p-3 sm:p-4"
          >
            <Outlet />
          </section>
        </div>
        {/* Docked queue panel — rendered once at shell level so it
         * stays visible (and uploads keep running) across route
         * changes. Hidden by the panel itself when the queue is empty. */}
        <ShellUploadQueuePanel />
      </main>
    </UploadQueueProvider>
  );
}

/** Renders the docked upload-queue panel from the shell-level queue
 * context. Split out so it can call `useUploadQueueContext()` inside
 * the provider. */
function ShellUploadQueuePanel() {
  const queue = useUploadQueueContext();
  return <UploadQueuePanel queue={queue} />;
}

function activeSectionForPath(path: string): AdminSectionId {
  // `/admin/foo/bar` → `foo`. Default to overview so an unknown
  // sub-path doesn't crash the sidebar's active highlight.
  const after = path.replace(/^\/admin\/?/, "");
  const head = after.split("/")[0] as AdminSectionId | "";
  const known = ADMIN_SECTIONS.find((s) => s.id === head);
  return known ? known.id : "overview";
}
